import { Link } from "react-router-dom";
import { Users, Eye } from "lucide-react";
import { useRadio } from "@/context/RadioContext";
import { Visualizer } from "./Visualizer";
import { FlameButton } from "./FlameButton";
import { ReactionChips } from "./ReactionChips";
import { PlayButton } from "./RadioPlayer";
import { Cover } from "./Bits";
import { Parallax, Spotlight } from "./Motion";

const Progress = ({ cur }) => {
  if (!cur) return null;
  const total = (new Date(cur.end) - new Date(cur.start)) / 1000;
  const pct = Math.min(100, ((Date.now() - new Date(cur.start)) / 1000 / total) * 100);
  return <div className="h-1 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full bg-gradient-to-r from-[#00F5D4] via-[#FFB800] to-[#FF2E63]" style={{ width: `${pct}%` }} /></div>;
};

export const NowHero = ({ compact = false }) => {
  const { state, playing } = useRadio();
  const cur = state?.current;
  return (
    <section className="noise relative overflow-hidden grid-bg" data-testid="now-hero">
      <Spotlight />
      <Parallax speed={0.35} className="pointer-events-none absolute -left-40 top-20"><div className="h-96 w-96 rounded-full bg-[#FF2E63]/20 blur-[120px]" /></Parallax>
      <Parallax speed={-0.25} className="pointer-events-none absolute -right-20 bottom-0"><div className="h-96 w-96 rounded-full bg-[#00F5D4]/10 blur-[120px]" /></Parallax>
      <div className={`relative mx-auto grid max-w-7xl items-center gap-10 px-4 md:px-8 lg:grid-cols-12 ${compact ? "pb-14 pt-28" : "min-h-[92vh] pb-20 pt-28"}`}>
        <div className="lg:col-span-7">
          <div className="fade-up mb-6 flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-2 rounded-full border border-[#FF2E63]/40 bg-[#FF2E63]/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[.25em] text-[#FF2E63]"><span className="live-dot" />On air 24/7</span>
            {state?.listeners >= 3 && <span className="flex items-center gap-1.5 font-mono text-xs text-slate-400" data-testid="hero-listeners"><Users className="h-3.5 w-3.5 text-[#00F5D4]" />{state.listeners} listening now</span>}
          </div>
          {cur?.hidden && <div className="fade-up d1 mb-2 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#FFB800]"><Eye className="h-4 w-4" />Blind Drop — who is it? Flame to reveal</div>}
          <h1 data-testid="hero-track-title" className="fade-up d1 glitch font-display text-5xl font-black uppercase leading-[.9] tracking-tight sm:text-6xl lg:text-7xl">
            {cur ? cur.title : state ? "Warming up" : "Tuning in"}
          </h1>
          {state && !cur && <p className="fade-up d2 mt-4 max-w-lg text-slate-400" data-testid="hero-empty">We're selecting the first artists right now. The broadcast starts as soon as the first tracks are approved — <a href="/join" className="text-[#00F5D4]">are you an artist?</a></p>}
          <div className="fade-up d2 mt-4 font-display text-xl text-slate-300 sm:text-2xl" data-testid="hero-artist-name">
            {cur?.hidden ? <span className="stroke-text font-black tracking-widest">??????</span> : cur?.artist && <Link to={`/a/${cur.artist.slug}?play=${cur.play_id}`} className="hover:text-[#00F5D4]">{cur.artist.stage_name}{cur.artist.city ? ` · ${cur.artist.city}` : ""}</Link>}
          </div>
          <div className="fade-up d3 mt-8 flex flex-wrap items-center gap-4">
            <PlayButton size={68} testid="hero-play-button" />
            <FlameButton size="lg" testid="hero-flame-button" />
            {!playing && <span className="text-sm text-slate-500">Tap play to join the broadcast. Flames unlock after 30 seconds of listening.</span>}
          </div>
          <div className="fade-up d4 mt-8 max-w-xl"><Progress cur={cur} /></div>
          <div className="fade-up d4 mt-6"><ReactionChips /></div>
        </div>
        <div className="fade-up d2 relative mx-auto w-full max-w-md lg:col-span-5"><Parallax speed={0.12}><div className="float-y">
          <div className="console-corners relative aspect-square rounded-full p-6">
            <div className={`vinyl absolute inset-0 rounded-full spin-slow ${playing ? "" : "paused"}`} />
            <Cover src={cur?.cover} hidden={cur?.hidden} className={`relative h-full w-full rounded-full border-4 border-[#07090E] shadow-2xl ${playing ? "spin-slow" : "spin-slow paused"}`} testid="hero-cover" />
            <div className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-[#07090E] bg-[#FF2E63]" />
          </div>
          <div className="mt-6 h-20"><Visualizer active={playing} bars={42} className="h-20" testid="hero-visualizer" /></div>
        </div></Parallax></div>
      </div>
    </section>
  );
};
